import { Component } from '@angular/core';
import { Auth, createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, updateProfile } from '@angular/fire/auth';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { map, pipe, startWith } from 'rxjs';

const isInvalid = pipe(
  map(status => status !== 'VALID'),
  startWith(true),
)

@Component({
    selector: 'is-login',
    templateUrl: './login.component.html',
    styleUrls: [`./login.component.scss`],
    standalone: false
})
export class LoginComponent {
  loading = false
  passwordResetSent = false
  loginForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
  })
  registerForm = this.fb.group({
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmation: ['', [Validators.required, Validators.minLength(8)]],
  })
  resetPasswordForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  })

  loginDisabled$ = this.loginForm.statusChanges.pipe(isInvalid)
  registerDisabled$ = this.registerForm.statusChanges.pipe(
    isInvalid,
    map(isInvalid => isInvalid || this.registerForm.value.password !== this.registerForm.value.confirmation),
  )
  resetPasswordDisabled$ = this.resetPasswordForm.statusChanges.pipe(isInvalid)

  constructor(
    private auth: Auth,
    private router: Router,
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
  ) {}

  async onLogin() {
    const {email, password} = this.loginForm.value
    try {
      this.loading = true
      await signInWithEmailAndPassword(this.auth, email, password)
      await this.router.navigate(['operations'])
    // eslint-able-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      this.openSnackBar(err.message)
    } finally {
      this.loading = false
    }
  }

  async onRegister() {
    const {name, email, password} = this.registerForm.value
    try {
      this.loading = true
      const {user} = await createUserWithEmailAndPassword(this.auth, email, password)
      await Promise.all([
        updateProfile(user, {displayName: name}),
+       sendEmailVerification(user),
      ])
      // TODO: update Firestore user with displayName
    // eslint-able-next-line @typescript-eslint/no-explicit-any  
    } catch (err: any) {
      this.openSnackBar(err.message)
    } finally {
      this.loading = false
    }
  }

  async onResetPassword() {
    const {email} = this.resetPasswordForm.value
    try {
      this.loading = true
      await sendPasswordResetEmail(this.auth, email)
      this.passwordResetSent = true
    // eslint-able-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      this.openSnackBar(err.message)
    } finally {
      this.loading = false
    }
  }

  private openSnackBar(message: string) {
    this.snackBar.open(message, 'dismiss', {duration: 5000});
  }
}
